/**
 * `<AuthHandoffRoute>` — `/auth/handoff`
 *
 * Terminus of the OAuth (Google / WorkOS) redirect chain. The API's
 * `/api/auth/callback` 302s here with the session JWT in the URL hash:
 *
 *   /auth/handoff#token=<jwt>&next=/w/<slug>/
 *
 * Hash fragments are NOT sent to the server, NOT logged in access logs,
 * and NOT placed in the `Referer` header — they're the safest channel
 * for ferrying a credential through a browser redirect.
 *
 * This route:
 *   1. Parses `token` + `next` from `window.location.hash`.
 *   2. Persists the token via {@link tokenStore} (i.e., `localStorage`).
 *   3. Strips the hash via `history.replaceState` so the token doesn't
 *      sit in browser history / the back button.
 *   4. Refreshes the `/api/me` query so `<RequireAuth>` flips from anon
 *      to authenticated atomically with the navigation.
 *   5. Navigates to `next` (or `/login?error=missing_token` if the hash
 *      is malformed).
 *
 * Per the auth-redesign (cookies removed). See the corresponding API
 * handler in `packages/auth/src/server/nestjs/controllers/auth.controller.ts`
 * (`callback`).
 */

import { useAuth, tokenStore } from "@cometchat-ai/auth/client";
import { Loader2 } from "../components/icons";
import { useEffect } from "react";
import { useNavigate } from "react-router";

export function AuthHandoffRoute() {
  const navigate = useNavigate();
  const auth = useAuth();

  useEffect(() => {
    const raw = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(raw);
    const token = params.get("token");
    const next = params.get("next") ?? "/";

    // Strip the hash IMMEDIATELY — before any other navigation or async
    // work — so a back-button press can't replay the credential.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);

    if (!token) {
      navigate("/login?error=missing_token", { replace: true });
      return;
    }

    tokenStore.set(token);
    // Refresh `/api/me` so <RequireAuth> sees the new principal on the
    // very next render and doesn't bounce the user back to /login.
    void auth.refresh().finally(() => {
      navigate(next, { replace: true });
    });
  }, [navigate, auth]);

  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
    </div>
  );
}
